import { useContext, useState } from "react";
import { useEffect } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import EnterpriseService from "../../common/services/enterprise-service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import LoadingOverlay from "react-loading-overlay";
import { confirmWrapper } from "../ui/askConfirm";
import { EnterpriseContext } from "./EnterpriseContext";
import EnterpriseNavBar from "./EnterpriseNavBar";
import { useNavigate, useParams } from "react-router";
import Tips from "../Tips";
import tipsList from "../../common/constants/tipsList";
import VehiclesTable from "./VehiclesTable";
import truckyService from "../../common/services/trucky-service";
import { useSearchParams } from "react-router-dom";
import MaintenanceAlerts from "./MaintenanceAlerts";
import BoxDynamicAd from "../../ads/BoxDynamicAd";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";

const FleetManagement = ({ eventBus, container }) => {
  const { globalContext, setGlobalContext } = useContext(EnterpriseContext);

  const company = globalContext.company;
  const member = globalContext.member;

  const [loading, setLoading] = useState(false);
  const es = new EnterpriseService();
  const [garages, setGarages] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const navigate = useNavigate();
  const [ownedModels, setOwnedModels] = useState([]);
  const { status } = useParams();
  const [searchParameters, setSearchParameters] = useState(
    truckyService.loadSearchParameters(
      "enterprise_fleet",
      {
        model_id: "",
        garage_id: "",
        orderBy: "",
        status: status ? status : "",
        sortingDirection: null,
        sortingField: null,
      },
      { status: status }
    )
  );

  useEffect(() => {
    truckyService.saveSearchParameters("enterprise_fleet", searchParameters);
  }, [searchParameters]);

  useEffect(() => {
    truckyService.setDocumentTitle("Fleet Management");

    getGarages();
    getVehicles();
    getOwnedModels();
  }, []);

  useEffect(async () => {
    getVehicles();
  }, [searchParameters]);

  const getVehicles = async () => {
    setLoading(true);
    const result = await es.getVehicles(company, searchParameters);
    setVehicles(result);
    setLoading(false);
  };

  const getGarages = async () => {
    const result = await es.getGarages(company);
    if (!result.message) setGarages(result);
  };

  const getOwnedModels = async () => {
    const result = await es.getOwnedModels(company);
    setOwnedModels(result);
  };

  const sell = async (vehicle) => {
    if (
      await confirmWrapper(container.localeManager.strings.sellVehicleConf, {
        container: container,
      })
    ) {
      setLoading(true);
      const result = await es.deleteVehicle(company, vehicle);
      if (result.success) {
        await getVehicles();
        getOwnedModels();
        toast.success(container.localeManager.strings.vehicleSold);
      } else {
        toast.error(result.message);
      }
      setLoading(false);
    }
  };

  const changeSortingField = ({ field, direction }) => {
    setSearchParameters({
      ...searchParameters,
      sortingField: field,
      sortingDirection: direction,
    });
  };

  return (
    <LoadingOverlay
      active={loading}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/vehicles"} container={container}></VTCMenu>
        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/vehicles",
                        title: container.localeManager.strings.fleet,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="page-hero mb-4">
                    <span className="page-hero-icon">
                      <FontAwesomeIcon icon="truck" className="mx-0" />
                    </span>
                    <div className="flex-1">
                      <h1 className="page-hero-title mb-0">{container.localeManager.strings.vehicles}</h1>
                    </div>
                    {!loading && es.can(member, "vehicles.buy") && (
                      <div className="ms-auto">
                        <Button size="sm" onClick={() => navigate("/vehicles/buy")} color="primary">
                          <FontAwesomeIcon icon="cart-plus" className="me-1" />{container.localeManager.strings.buyANewVehicle}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="filters-toolbar mb-3">
                  <FormGroup row className="mb-3">
                    <Col>
                      <Label>{container.localeManager.strings.models}</Label>
                      <Input
                        value={searchParameters.model_id}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            model_id: e.target.value,
                          })
                        }
                        type="select"
                      >
                        <option value="">
                          {container.localeManager.strings.all}
                        </option>
                        {ownedModels.map((m) => {
                          return (
                            <option value={m.id}>
                              {m.brand_name} {m.model_name}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col>
                      <Label>{container.localeManager.strings.garages}</Label>
                      <Input
                        value={searchParameters.garage_id}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            garage_id: e.target.value,
                          })
                        }
                        type="select"
                      >
                        <option value="">
                          {container.localeManager.strings.all}
                        </option>
                        {garages.map((m) => {
                          return <option value={m.id}>{m.city.name}</option>;
                        })}
                      </Input>
                    </Col>
                    <Col>
                      <Label>{container.localeManager.strings.status}</Label>
                      <Input
                        value={searchParameters.status}
                        onChange={(e) =>
                          setSearchParameters({
                            ...searchParameters,
                            status: e.target.value,
                          })
                        }
                        type="select"
                      >
                        <option value="">
                          {container.localeManager.strings.all}
                        </option>
                        <option value="active">
                          {container.localeManager.strings.active}
                        </option>
                        <option value="need_maintenance">
                          {container.localeManager.strings.need_maintenance}
                        </option>
                        <option value="under_maintenance">
                          {container.localeManager.strings.under_maintenance}
                        </option>
                        <option value="requested">
                          {container.localeManager.strings.requested}
                        </option>
                      </Input>
                    </Col>
                  </FormGroup>
                  </div>
                  {!loading && vehicles.length == 0 && (
                    <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-6 mb-3">
                      {container.localeManager.strings.garageEmpty}
                    </div>
                  )}
                  {!loading && vehicles.length > 0 && (
                    <div>
                      <VehiclesTable
                        sell={sell}
                        vehicles={vehicles}
                        company={company}
                        member={member}
                        container={container}
                        changeSortingField={changeSortingField}
                        filters={searchParameters}
                      ></VehiclesTable>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
            <aside className="ad-column-placeholder">
              <Tips
                tipKey={tipsList.KEYS.ENTERPRISE_VEHICLES}
                localeManager={container.localeManager}
              ></Tips>
              <MaintenanceAlerts
                company={company}
                container={container}
              ></MaintenanceAlerts>
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          </Row>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Col>
      </Row>
    </LoadingOverlay>
  );
};

export default FleetManagement;
